import React from 'react';

// Configuração visual das condições/laudos médicos conhecidos
const CONFIG_LAUDOS = {
  TEA: {
    label: 'TEA',
    icone: '🧩',
    descricao: 'Transtorno do Espectro Autista',
    estilo: 'bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800/80',
  },
  TDAH: {
    label: 'TDAH',
    icone: '⚡',
    descricao: 'Déficit de Atenção e Hiperatividade',
    estilo: 'bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800/80',
  },
  TOD: {
    label: 'TOD',
    icone: '🎯',
    descricao: 'Transtorno Opositor Desafiador',
    estilo: 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800/80',
  },
  PCD: {
    label: 'PCD',
    icone: '♿',
    descricao: 'Pessoa com Deficiência / Mobilidade Reduzida',
    estilo: 'bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/80',
  },
  AUDITIVA: {
    label: 'Auditiva',
    icone: '🦻',
    descricao: 'Deficiência Auditiva / Surdez',
    estilo: 'bg-teal-50 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300 border-teal-200 dark:border-teal-800/80',
  },
  VISUAL: {
    label: 'Visual',
    icone: '👁️',
    descricao: 'Baixa Visão / Deficiência Visual',
    estilo: 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800/80',
  },
  DOWN: {
    label: 'Down',
    icone: '🧬',
    descricao: 'Síndrome de Down',
    estilo: 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/80',
  },
};

export const LAUDOS_DISPONIVEIS = [
  { id: 'TEA', label: 'TEA (Autismo)', icone: '🧩' },
  { id: 'TDAH', label: 'TDAH', icone: '⚡' },
  { id: 'TOD', label: 'TOD', icone: '🎯' },
  { id: 'PCD', label: 'PCD (Física)', icone: '♿' },
  { id: 'AUDITIVA', label: 'Deficiência Auditiva', icone: '🦻' },
  { id: 'VISUAL', label: 'Deficiência Visual', icone: '👁️' },
  { id: 'DOWN', label: 'Síndrome de Down', icone: '🧬' },
];

export function parseLaudos(valor) {
  if (!valor) return [];
  if (Array.isArray(valor)) return valor.filter(Boolean);
  if (typeof valor === 'string') {
    try {
      const parsed = JSON.parse(valor);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      // Se for string separada por vírgula ou única
      return valor.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
}

/**
 * Exibe os selos de Líder Servidor e Laudos Médicos ao lado do nome do aluno.
 * Bolsista NÃO é exibido aqui (campo estritamente administrativo de gestão).
 */
export default function AlunoBadges({ aluno, compact = false, className = '' }) {
  if (!aluno) return null;

  const ehLider = Boolean(aluno.lider_servidor === 1 || aluno.lider_servidor === true || aluno.lider_servidor === '1');
  const laudos = parseLaudos(aluno.laudos);
  const cuidados = aluno.cuidados_medicos ? String(aluno.cuidados_medicos).trim() : '';

  if (!ehLider && laudos.length === 0 && !cuidados) {
    return null;
  }

  return (
    <div className={`inline-flex flex-wrap items-center gap-1 align-middle ${className}`}>
      {/* Selo: Líder Servidor */}
      {ehLider && (
        <span
          title="Líder Servidor: Aluno ajudante e referência positiva dos educadores e monitores"
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-amber-50 dark:bg-amber-950/70 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800/80 shadow-2xs select-none"
        >
          <span>⭐</span>
          <span>{compact ? 'Líder' : 'Líder Servidor'}</span>
        </span>
      )}

      {/* Selos de Laudos / Condições */}
      {laudos.map((item) => {
        const chave = String(item).toUpperCase();
        const cfg = CONFIG_LAUDOS[chave] || {
          label: item,
          icone: '📋',
          descricao: `Condição médica / laudo: ${item}`,
          estilo: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700',
        };

        const tituloTooltip = cuidados
          ? `${cfg.descricao} — Cuidados: ${cuidados}`
          : cfg.descricao;

        return (
          <span
            key={chave}
            title={tituloTooltip}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border shadow-2xs select-none ${cfg.estilo}`}
          >
            <span>{cfg.icone}</span>
            <span>{cfg.label}</span>
          </span>
        );
      })}

      {/* Indicador de Cuidados Médicos / Pedagógicos (caso não tenha laudo específico ou complementar) */}
      {cuidados && laudos.length === 0 && (
        <span
          title={`Orientações pedagógicas e médicas: ${cuidados}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 select-none"
        >
          <span>💡</span>
          <span>Cuidados</span>
        </span>
      )}
    </div>
  );
}

